<template>
    <DModal
        :id="mailModalId"
        :show="showModal"
        :title="__(`tools.emails.samples.${emailType}.${sample}`)"
        is-big
        overflow-auto
        @close="closeModal"
    >
        <div class="modal-header">
            <DSelect
                id="brand"
                v-model="brand"
                :options="brandOptions"
                style="flex-grow: 1"
                class="m-0"
                @update:value="updateSelectedBrand"
            />
            <DSelect
                id="language"
                v-model="language"
                :options="languages"
                @update:value="handleLanguageChange"
            />
            <DButton
                :loading="loading"
                icon="eye"
                :label="__('tools.emails.modal.display')"
                :color="'primary'"
                @click="displayEmail()"
            />
            <DButton
                icon="download"
                :disabled="isInLoadingState || sampleData === null || !sampleData.eml"
                :label="__('tools.emails.modal.download')"
                @click="downloadSampleDocument()"
            />
        </div>

        <div
            v-if="!isInLoadingState && (sampleData === null || !sampleData.eml) && errors.length === 0"
            class="select-prompt"
        >
            <div>
                <DIcon
                    name="form-dropdown"
                    :size="32"
                />
                <p v-html="__('tools.emails.modal.select_a_tenant')" />
            </div>
        </div>

        <div
            v-if="sampleData || errors.length"
            class="modal-base"
        >
            <DTabs>
                <DTabItem
                    :label="__('tools.emails.modal.tabs.html')"
                    :active="currentTab === 0"
                    @click="changeTab(0)"
                />
                <DTabItem
                    :label="__('tools.emails.modal.tabs.plain_text')"
                    :active="currentTab === 1"
                    @click="changeTab(1)"
                />
                <DTabItem
                    :label="__('tools.emails.modal.tabs.send_copy')"
                    :active="currentTab === 2"
                    @click="changeTab(2)"
                />
            </DTabs>

            <EmailPreviewEnvelopeDetails
                :sample-data="sampleData"
            />

            <DCard
                v-if="currentTab === 0"
                class="ignore-parent-styles"
            >
                <div v-html="divFromHTMLBody()" />
            </DCard>

            <DCard
                v-if="currentTab === 1"
                padding
            >
                <div
                    class="raw-textbox"
                    v-html="sampleData.text"
                />
            </DCard>

            <DCard
                v-if="currentTab === 2"
                padding
            >
                <div class="mt-3">
                    <DInput
                        id="email"
                        v-model="email"
                        :label="__('tools.emails.modal.email')"
                        add-label-classes="required"
                    />
                    <p
                        v-for="(error, index) in errors"
                        :key="index"
                        class="help-block text-danger"
                    >
                        {{ error }}
                    </p>
                    <div class="d-flex justify-content-start align-items-center">
                        <DButton
                            :loading="sendingEmailLoading"
                            :label="__('tools.emails.modal.tabs.send_copy')"
                            :disabled="!email"
                            :color="'primary'"
                            @click="sendEmail()"
                        />
                        <p
                            v-show="mailSentSuccessfully"
                            class="text-success ms-3 mb-0 p-0"
                        >
                            {{ __('tools.emails.modal.mail_sent') }}
                        </p>
                    </div>
                </div>
            </DCard>
        </div>

        <div
            v-if="errors.length > 0 && !email"
            class="modal-error"
        >
            <DAlert
                v-for="(error, index) in errors"
                :key="index"
                color="danger"
            >
                {{ error }}
            </DAlert>
        </div>
    </DModal>
</template>

<script>
/* eslint vue/no-v-html: 0 */

import NProgress from 'nprogress'
import preventEmailUrl from '~@/Mixins/preventEmailUrl'
import EmailPreviewEnvelopeDetails from '~@/Components/Tools/Emails/EmailPreviewEnvelopeDetails.vue'

export default {
    name: 'EmailPreviewModal',
    components: {
        EmailPreviewEnvelopeDetails
    },
    mixins: [
        preventEmailUrl
    ],
    props: {
        showModal: {
            type: Boolean,
            default: false
        },
        emailType: {
            type: String,
            default: ''
        },
        sample: {
            type: String,
            default: ''
        },
        brands: {
            type: Array,
            default: () => { return [] }
        },
        availableLanguages: {
            type: Array,
            default: () => { return [] }
        }
    },
    emits: ['closeModal'],
    data () {
        return {
            brand: this.brands[0]?.id,
            loading: false,
            sendingEmailLoading: false,
            mailSentSuccessfully: false,
            errors: [],
            sampleData: null,
            fileName: null,
            email: '',
            currentTab: 0,
            mailModalId: 'mailModal',
            language: null,
            languages: []
        }
    },
    computed: {
        brandOptions () {
            return this.brands.map(brand => {
                return {
                    value: brand.id,
                    label: brand.name
                }
            })
        }
    },
    updated () {
        this.preventEmailUrl(this.mailModalId)
    },
    mounted () {
        this.languages = this.getAvailableLanguages()
        this.language = this.getAvailableLanguages()[0].value
    },
    methods: {
        changeTab (tabIndex) {
            this.currentTab = tabIndex
        },
        displayEmail () {
            NProgress.start()

            this.loading = true
            this.sampleData = null
            this.errors = []

            window.axios.post(this.$route('tools.emails.show-sample-email'), {
                emailType: this.emailType,
                sample: this.sample,
                brand: this.brand,
                language: this.language
            })
                .then(response => {
                    NProgress.done()

                    this.loading = false
                    this.sampleData = response.data
                })
                .catch((error) => {
                    NProgress.done()

                    const errorObject = error.response && error.response.data && error.response.data.errors
                        ? error.response.data.errors
                        : {}
                    Object.keys(errorObject).forEach((field) => {
                        errorObject[field].forEach((message) => {
                            this.errors.push(message)
                        })
                    })

                    this.loading = false
                })
        },
        downloadSampleDocument () {
            const link = document.createElement('a')
            link.download = 'example.eml'
            const blob = new Blob([this.sampleData.eml], {
                type: 'text/plain'
            })
            link.href = window.URL.createObjectURL(blob)
            link.click()
        },
        sendEmail () {
            NProgress.start()

            this.sendingEmailLoading = true
            this.mailSentSuccessfully = false
            this.errors = []

            window.axios.post(this.$route('tools.emails.send-email'), {
                emailType: this.emailType,
                sample: this.sample,
                brand: this.brand,
                email: this.email,
                language: this.language
            })
                .then(() => {
                    NProgress.done()

                    this.sendingEmailLoading = false
                    this.mailSentSuccessfully = true
                })
                .catch((error) => {
                    NProgress.done()

                    const errorObject = error.response && error.response.data && error.response.data.errors
                        ? error.response.data.errors
                        : {}
                    Object.keys(errorObject).forEach((field) => {
                        errorObject[field].forEach((message) => {
                            this.errors.push(message)
                        })
                    })

                    this.sendingEmailLoading = false
                })
        },
        closeModal () {
            this.sampleData = null
            this.fileName = null
            this.errors = []
            this.email = ''
            this.mailSentSuccessfully = false

            this.$emit('closeModal')
        },
        divFromHTMLBody () {
            return this.sampleData?.html.split('</head>')[1].split('</html>')[0]
                .replace('<body ', '<div ')
                .replace('</body>', '</div>')
        },
        updateSelectedBrand (item) {
            const brandId = parseInt(item)
            this.brand = brandId
            this.languages = this.getAvailableLanguages()
            this.language = this.brands.filter(brand => brand.id === brandId)[0].default_customer_language
        },
        handleLanguageChange (item) {
            this.language = item
        },
        getAvailableLanguages () {
            const lang = []

            this.availableLanguages.forEach((item) => {
                if (item.brand_id === this.brand) {
                    lang.push(item.languages)
                }
            })

            return lang[0]
        }
    }
}
</script>

<style lang="scss" scoped>
.modal-header {
    display: flex;
    grid-gap: 15px;
    padding: 15px;
    align-items: center;
}
.modal-base {
    margin: 15px;
    margin-top: 0px;
    display: grid;
    grid-gap: 15px;

    & .raw-textbox {
        font-family: monospace, monospace;
        white-space: pre-wrap;
    }
}
.modal-error {
    margin: 15px;
    margin-top: 0px;
    display: grid;
    grid-gap: 15px;
}

.select-prompt {
    height: 250px;
    display: flex;
    align-items: center;
    justify-content: center;

    & div {
        display: flex;
        flex-direction: column;
        grid-gap: 15px;
        align-items: center;

        & p {
            text-align: center;
            line-height: 1.5em;
            color: rgba(0,0,0,0.6);
        }
    }
}
</style>
